import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import productService from '../../../services/productService';
import barcodeService from '../../../services/barcodeService';
import categoryService from '../../../services/categoryService';
import subCategoryService from '../../../services/subCategoryService';
import * as XLSX from 'xlsx';
import './ProductQRManagement.css';

const ProductQRManagement = () => {
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [quantity, setQuantity] = useState(1);
    const [barcodeHistory, setBarcodeHistory] = useState(null);
    const [generatedBarcodes, setGeneratedBarcodes] = useState(null);
    const [generationType, setGenerationType] = useState('base');
    const [variantQuantities, setVariantQuantities] = useState([]);
    const [activeTab, setActiveTab] = useState('products');

    const [filters, setFilters] = useState({
        search: '',
        category: '',
        subCategory: '',
        brand: '',
        status: ''
    });

    useEffect(() => {
        loadProducts();
        loadCategories();
    }, []);

    useEffect(() => {
        if (filters.category) {
            loadSubCategories(filters.category);
        } else {
            setSubCategories([]);
            setFilters(prev => ({ ...prev, subCategory: '' }));
        }
    }, [filters.category]);

    useEffect(() => {
        filterProducts();
    }, [products, filters]);

    const loadProducts = async () => {
        try {
            setLoading(true);
            const response = await productService.getAllProducts();
            const productsWithVariants = response.data.map(product => ({
                ...product,
                has_variants: product.variants && product.variants.length > 0
            }));
            setProducts(productsWithVariants);
        } catch (error) {
            Swal.fire('Error!', error.message || 'Failed to load products', 'error');
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const response = await categoryService.getAllCategories();
            setCategories(response.data);
        } catch (error) {
            console.error('Failed to load categories:', error);
        }
    };

    const loadSubCategories = async (categoryId) => {
        try {
            const response = await subCategoryService.getAllSubCategories();
            const filteredSubCategories = response.data.filter(
                sub => sub.category_id.toString() === categoryId
            );
            setSubCategories(filteredSubCategories);
        } catch (error) {
            console.error('Failed to load sub-categories:', error);
        }
    };

    const filterProducts = () => {
        let filtered = products;

        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            filtered = filtered.filter(product =>
                product.name.toLowerCase().includes(searchLower) ||
                product.sku.toLowerCase().includes(searchLower) ||
                (product.brand && product.brand.toLowerCase().includes(searchLower))
            );
        }

        if (filters.category) {
            filtered = filtered.filter(product =>
                product.sub_category?.category_id.toString() === filters.category
            );
        }

        if (filters.subCategory) {
            filtered = filtered.filter(product =>
                product.sub_category_id.toString() === filters.subCategory
            );
        }

        if (filters.brand) {
            filtered = filtered.filter(product =>
                product.brand && product.brand.toLowerCase().includes(filters.brand.toLowerCase())
            );
        }

        if (filters.status) {
            filtered = filtered.filter(product =>
                filters.status === 'active' ? product.is_active : !product.is_active
            );
        }

        setFilteredProducts(filtered);
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const clearFilters = () => {
        setFilters({
            search: '',
            category: '',
            subCategory: '',
            brand: '',
            status: ''
        });
    };

    const handleGenerateBarcode = (product) => {
        setSelectedProduct(product);
        setQuantity(1);
        setGenerationType(product.has_variants ? 'variants' : 'base');
        
        if (product.has_variants && product.variants) {
            const initialQuantities = product.variants.map(variant => ({
                variant_id: variant.id,
                variant_name: variant.variant_name,
                quantity: 1
            }));
            setVariantQuantities(initialQuantities);
        } else {
            setVariantQuantities([]);
        }
        
        setShowGenerateModal(true);
    };

    const handleVariantQuantityChange = (variantId, newQuantity) => {
        setVariantQuantities(prev => 
            prev.map(item => 
                item.variant_id === variantId 
                    ? { ...item, quantity: Math.max(1, newQuantity) }
                    : item
            )
        );
    };

    const handleGenerateSubmit = async () => {
        try {
            let requestData = {};
            
            if (generationType === 'base') {
                if (quantity < 1) {
                    Swal.fire('Error!', 'Quantity must be at least 1', 'error');
                    return;
                }
                requestData = { quantity };
            } else {
                const variantsData = variantQuantities.map(item => ({
                    variant_id: item.variant_id,
                    quantity: item.quantity
                }));
                
                if (variantsData.length === 0) {
                    Swal.fire('Error!', 'Please add at least one variant', 'error');
                    return;
                }
                
                requestData = { variants: variantsData };
            }

            const response = await barcodeService.generateBarcodes(selectedProduct.id, requestData);
            setGeneratedBarcodes(response.data);
            setShowGenerateModal(false);
            setActiveTab('barcodes');
            
            const totalQuantity = generationType === 'base' 
                ? quantity 
                : variantQuantities.reduce((sum, item) => sum + item.quantity, 0);
            
            Swal.fire({
                title: 'Success!',
                text: `${totalQuantity} barcodes generated successfully`,
                icon: 'success',
                confirmButtonText: 'View Barcodes'
            });
        } catch (error) {
            Swal.fire('Error!', error.message || 'Failed to generate barcodes', 'error');
        }
    };

    const handleViewHistory = async (product) => {
        try {
            setSelectedProduct(product);
            const response = await barcodeService.getProductBarcodes(product.id);
            setBarcodeHistory(response.data);
            setShowHistoryModal(true);
        } catch (error) {
            Swal.fire('Error!', error.message || 'Failed to load barcode history', 'error');
        }
    };

    const closeGeneratedBarcodes = () => {
        setGeneratedBarcodes(null);
        setActiveTab('products');
        loadProducts();
    };

    // Export to Excel function
    const exportToExcel = () => {
        if (!generatedBarcodes) {
            Swal.fire('Info', 'No barcodes generated to export', 'info');
            return;
        }

        try {
            const isVariantResponse = generatedBarcodes.variants !== undefined;
            const product = generatedBarcodes.product;
            const allBarcodes = isVariantResponse 
                ? generatedBarcodes.variants.flatMap(variant => variant.barcodes)
                : generatedBarcodes.barcodes;

            // Prepare data for Excel
            const excelData = allBarcodes.map(barcode => ({
                'Title': product.name,
                'Description': barcode.variant ? barcode.variant.variant_name : 'Base Product',
                'Code': barcode.barcode_number
            }));

            // Create workbook and worksheet
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(excelData);

            // Set column widths
            const colWidths = [
                { wch: 30 }, // Title
                { wch: 25 }, // Description
                { wch: 20 }  // Code
            ];
            ws['!cols'] = colWidths;

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(wb, ws, 'Barcodes');

            // Generate Excel file
            const fileName = `barcodes_${product.name.replace(/[^a-z0-9]/gi, '_')}_${new Date().getTime()}.xlsx`;
            XLSX.writeFile(wb, fileName);

            Swal.fire('Success!', `Exported ${excelData.length} barcodes to Excel`, 'success');
        } catch (error) {
            console.error('Excel export error:', error);
            Swal.fire('Error!', 'Failed to export to Excel', 'error');
        }
    };

    // BoxHero integration
    const openBoxHeroPrint = () => {
        if (!generatedBarcodes) {
            Swal.fire({
                title: 'No Barcodes Generated',
                text: 'Please generate barcodes first before using BoxHero print',
                icon: 'info',
                confirmButtonText: 'OK'
            });
            return;
        }

        Swal.fire({
            title: 'BoxHero Print Instructions',
            html: `
                <div style="text-align: left;">
                    <h4>Follow these steps to print with BoxHero:</h4>
                    <ol>
                        <li>Click "Open BoxHero" below</li>
                        <li>Sign up or log in to BoxHero account</li>
                        <li>Go to "Print" section</li>
                        <li>Upload the Excel file we generated</li>
                        <li>Select your Xprinter XP-T361U</li>
                        <li>Set paper size to 40×30mm</li>
                        <li>Configure label template as needed</li>
                        <li>Print your barcodes</li>
                    </ol>
                    <p><strong>First, let's generate the Excel file:</strong></p>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: 'Generate Excel & Open BoxHero',
            cancelButtonText: 'Cancel',
            showDenyButton: true,
            denyButtonText: 'Just Open BoxHero',
            icon: 'info'
        }).then((result) => {
            if (result.isConfirmed) {
                // Generate Excel first, then open BoxHero
                exportToExcel();
                setTimeout(() => {
                    window.open('https://www.boxhero.io/print', '_blank');
                    showBoxHeroInstructions();
                }, 1000);
            } else if (result.isDenied) {
                // Just open BoxHero
                window.open('https://www.boxhero.io/print', '_blank');
                showBoxHeroInstructions();
            }
        });
    };

    const showBoxHeroInstructions = () => {
        Swal.fire({
            title: 'BoxHero Setup Guide',
            html: `
                <div style="text-align: left; max-height: 400px; overflow-y: auto;">
                    <h4>📋 Complete Setup Instructions:</h4>
                    
                    <h5>Step 1: Account Setup</h5>
                    <ul>
                        <li>Create account at <a href="https://www.boxhero.io" target="_blank">boxhero.io</a></li>
                        <li>Verify your email address</li>
                        <li>Complete profile setup</li>
                    </ul>

                    <h5>Step 2: Printer Configuration</h5>
                    <ul>
                        <li>Go to Settings → Printers</li>
                        <li>Add new printer: Xprinter XP-T361U</li>
                        <li>Set paper size: 40mm × 30mm</li>
                        <li>Select thermal label type</li>
                    </ul>

                    <h5>Step 3: Label Template</h5>
                    <ul>
                        <li>Go to Templates → Create New</li>
                        <li>Name: "40x30 Barcode"</li>
                        <li>Set dimensions: 40mm × 30mm</li>
                        <li>Add fields: Title, Description, Code</li>
                        <li>Save template</li>
                    </ul>

                    <h5>Step 4: Import & Print</h5>
                    <ul>
                        <li>Download the Excel file we generated</li>
                        <li>Go to BoxHero Print section</li>
                        <li>Upload the Excel file</li>
                        <li>Map columns: Title → A, Description → B, Code → C</li>
                        <li>Select your printer and template</li>
                        <li>Click Print!</li>
                    </ul>

                    <h5>💡 Pro Tips:</h5>
                    <ul>
                        <li>Use "Code 128" barcode format</li>
                        <li>Set barcode height to 18-20mm</li>
                        <li>Test print 1-2 labels first</li>
                        <li>Adjust font sizes if needed</li>
                    </ul>
                </div>
            `,
            width: 600,
            confirmButtonText: 'Got it!',
            icon: 'info'
        });
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="barcode-management-container">
                <div className="barcode-loading-screen">
                    <div className="barcode-loading-spinner"></div>
                    <h3>Loading Products...</h3>
                </div>
            </div>
        );
    }

    return (
        <div className="barcode-management-container">
            {/* Header */}
            <div className="barcode-header">
                <div className="barcode-header-content">
                    <div className="barcode-header-title">
                        <h1>Barcode Management</h1>
                        <p>Generate and manage product barcodes efficiently</p>
                    </div>
                    <div className="barcode-header-stats">
                        <div className="barcode-stat-card">
                            <span className="barcode-stat-number">{filteredProducts.length}</span>
                            <span className="barcode-stat-label">Products</span>
                        </div>
                        <button className="barcode-btn-refresh" onClick={loadProducts}>
                            <span className="barcode-icon">🔄</span>
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="barcode-navigation-tabs">
                <button 
                    className={`barcode-tab-button ${activeTab === 'products' ? 'barcode-tab-active' : ''}`}
                    onClick={() => setActiveTab('products')}
                >
                    📦 Products
                </button>
                <button 
                    className={`barcode-tab-button ${activeTab === 'barcodes' ? 'barcode-tab-active' : ''}`}
                    onClick={() => setActiveTab('barcodes')}
                    disabled={!generatedBarcodes}
                >
                    🏷️ Generated Barcodes
                </button>
            </div>

            {/* Main Content */}
            <div className="barcode-main-content">
                {activeTab === 'products' && (
                    <>
                        {/* Filters Section */}
                        <div className="barcode-filters-section">
                            <div className="barcode-filters-header">
                                <h3>Filters</h3>
                                <button className="barcode-btn-clear" onClick={clearFilters}>
                                    Clear All
                                </button>
                            </div>
                            <div className="barcode-filters-grid">
                                <div className="barcode-filter-group">
                                    <input
                                        type="text"
                                        placeholder="🔍 Search products..."
                                        value={filters.search}
                                        onChange={(e) => handleFilterChange('search', e.target.value)}
                                        className="barcode-search-input"
                                    />
                                </div>
                                <div className="barcode-filter-group">
                                    <select
                                        value={filters.category}
                                        onChange={(e) => handleFilterChange('category', e.target.value)}
                                    >
                                        <option value="">All Categories</option>
                                        {categories.map(category => (
                                            <option key={category.id} value={category.id}>
                                                {category.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="barcode-filter-group">
                                    <select
                                        value={filters.subCategory}
                                        onChange={(e) => handleFilterChange('subCategory', e.target.value)}
                                        disabled={!filters.category}
                                    >
                                        <option value="">All Sub-Categories</option>
                                        {subCategories.map(subCategory => (
                                            <option key={subCategory.id} value={subCategory.id}>
                                                {subCategory.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="barcode-filter-group">
                                    <select
                                        value={filters.status}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                    >
                                        <option value="">All Status</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Products Grid */}
                        <div className="barcode-products-grid">
                            {filteredProducts.map(product => (
                                <div key={product.id} className="barcode-product-card">
                                    <div className="barcode-product-header">
                                        <div className="barcode-product-badge">
                                            {product.has_variants ? '🔄' : '📦'}
                                        </div>
                                        <div className="barcode-product-status">
                                            <span className={`barcode-status-dot ${product.is_active ? 'barcode-status-active' : 'barcode-status-inactive'}`}></span>
                                            {product.is_active ? 'Active' : 'Inactive'}
                                        </div>
                                    </div>
                                    <div className="barcode-product-content">
                                        <h4 className="barcode-product-name">{product.name}</h4>
                                        <p className="barcode-product-sku">SKU: {product.sku}</p>
                                        <p className="barcode-product-brand">{product.brand || 'No Brand'}</p>
                                        <div className="barcode-product-category">
                                            {product.sub_category?.category?.name} → {product.sub_category?.name}
                                        </div>
                                        <div className="barcode-product-variants">
                                            <span className={`barcode-variant-indicator ${product.has_variants ? 'barcode-has-variants' : 'barcode-no-variants'}`}>
                                                {product.has_variants ? 
                                                    `${product.variants?.length || 0} variants` : 
                                                    'No variants'
                                                }
                                            </span>
                                        </div>
                                    </div>
                                    <div className="barcode-product-actions">
                                        <button
                                            className="barcode-btn-generate"
                                            onClick={() => handleGenerateBarcode(product)}
                                        >
                                            🏷️ Generate
                                        </button>
                                        <button
                                            className="barcode-btn-history"
                                            onClick={() => handleViewHistory(product)}
                                        >
                                            📋 History
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {filteredProducts.length === 0 && (
                            <div className="barcode-empty-state">
                                <div className="barcode-empty-icon">🏷️</div>
                                <h3>No Products Found</h3>
                                <p>Try adjusting your search or filters</p>
                                <button className="barcode-btn-primary" onClick={clearFilters}>
                                    Clear Filters
                                </button>
                            </div>
                        )}
                    </>
                )}

                {activeTab === 'barcodes' && generatedBarcodes && (
                    <BarcodeGeneratedView 
                        generatedBarcodes={generatedBarcodes}
                        onClose={closeGeneratedBarcodes}
                        onExportExcel={exportToExcel}
                        onBoxHeroPrint={openBoxHeroPrint}
                    />
                )}
            </div>

            {/* Generate Barcode Modal */}
            {showGenerateModal && selectedProduct && (
                <BarcodeGenerateModal
                    product={selectedProduct}
                    generationType={generationType}
                    setGenerationType={setGenerationType}
                    quantity={quantity}
                    setQuantity={setQuantity}
                    variantQuantities={variantQuantities}
                    setVariantQuantities={setVariantQuantities}
                    onClose={() => setShowGenerateModal(false)}
                    onSubmit={handleGenerateSubmit}
                />
            )}

            {/* Barcode History Modal */}
            {showHistoryModal && barcodeHistory && (
                <BarcodeHistoryModal
                    barcodeHistory={barcodeHistory}
                    onClose={() => setShowHistoryModal(false)}
                    formatDate={formatDate}
                />
            )}
        </div>
    );
};

// Generate Barcode Modal Component
const BarcodeGenerateModal = ({
    product,
    generationType,
    setGenerationType,
    quantity,
    setQuantity,
    variantQuantities,
    setVariantQuantities,
    onClose,
    onSubmit
}) => {
    const handleVariantQuantityChange = (variantId, newQuantity) => {
        setVariantQuantities(prev => 
            prev.map(item => 
                item.variant_id === variantId 
                    ? { ...item, quantity: Math.max(1, newQuantity) }
                    : item
            )
        );
    };

    const totalVariantQuantity = variantQuantities.reduce((sum, item) => sum + item.quantity, 0);

    return (
        <div className="barcode-modal-overlay">
            <div className="barcode-modal">
                <div className="barcode-modal-header">
                    <h2>Generate Barcodes</h2>
                    <button className="barcode-modal-close" onClick={onClose}>×</button>
                </div>
                <div className="barcode-modal-body">
                    <div className="barcode-product-preview">
                        <div className="barcode-product-image">
                            {product.name.charAt(0)}
                        </div>
                        <div className="barcode-product-details">
                            <h3>{product.name}</h3>
                            <p>SKU: {product.sku}</p>
                            <p>Brand: {product.brand || 'N/A'}</p>
                        </div>
                    </div>

                    {product.has_variants && (
                        <div className="barcode-generation-type">
                            <label>Generate for:</label>
                            <div className="barcode-type-selector">
                                <button
                                    className={`barcode-type-btn ${generationType === 'base' ? 'barcode-type-active' : ''}`}
                                    onClick={() => setGenerationType('base')}
                                >
                                    Base Product
                                </button>
                                <button
                                    className={`barcode-type-btn ${generationType === 'variants' ? 'barcode-type-active' : ''}`}
                                    onClick={() => setGenerationType('variants')}
                                >
                                    Variants
                                </button>
                            </div>
                        </div>
                    )}

                    {generationType === 'base' && (
                        <div className="barcode-quantity-input">
                            <label>Number of Barcodes</label>
                            <div className="barcode-input-group">
                                <input
                                    type="number"
                                    value={quantity}
                                    onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                                    min="1"
                                    max="1000"
                                />
                                <span className="barcode-input-label">barcodes</span>
                            </div>
                        </div>
                    )}

                    {generationType === 'variants' && (
                        <div className="barcode-variants-section">
                            <label>Variant Quantities</label>
                            <div className="barcode-variants-list">
                                {variantQuantities.map((item) => (
                                    <div key={item.variant_id} className="barcode-variant-item">
                                        <span className="barcode-variant-name">{item.variant_name}</span>
                                        <div className="barcode-quantity-control">
                                            <button
                                                onClick={() => handleVariantQuantityChange(item.variant_id, item.quantity - 1)}
                                                disabled={item.quantity <= 1}
                                            >
                                                -
                                            </button>
                                            <span>{item.quantity}</span>
                                            <button
                                                onClick={() => handleVariantQuantityChange(item.variant_id, item.quantity + 1)}
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="barcode-total-quantity">
                                Total: <strong>{totalVariantQuantity}</strong> barcodes
                            </div>
                        </div>
                    )}
                </div>
                <div className="barcode-modal-footer">
                    <button className="barcode-btn-secondary" onClick={onClose}>
                        Cancel
                    </button>
                    <button className="barcode-btn-primary" onClick={onSubmit}>
                        Generate Barcodes
                    </button>
                </div>
            </div>
        </div>
    );
};

// Barcode History Modal Component
const BarcodeHistoryModal = ({ barcodeHistory, onClose, formatDate }) => {
    const { product, barcodes, statistics } = barcodeHistory;
    const [selectedBarcodes, setSelectedBarcodes] = useState([]);

    const handleSelectAll = () => {
        if (selectedBarcodes.length === barcodes.length) {
            setSelectedBarcodes([]);
        } else {
            setSelectedBarcodes(barcodes.map(b => b.id));
        }
    };

    const printBarcodes = async () => {
        const barcodesToPrint = selectedBarcodes.length > 0
            ? barcodes.filter(b => selectedBarcodes.includes(b.id))
            : barcodes;

        if (barcodesToPrint.length === 0) {
            Swal.fire('Info', 'No barcodes selected to print', 'info');
            return;
        }

        // Load JsBarcode library dynamically
        if (!window.JsBarcode) {
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js';
            document.head.appendChild(script);
            script.onload = () => printBarcodesIndividual(barcodesToPrint);
        } else {
            printBarcodesIndividual(barcodesToPrint);
        }
    };

    const printBarcodesIndividual = (barcodesToPrint) => {
        Swal.fire({
            title: 'Printing',
            html: `Preparing to print ${barcodesToPrint.length} barcodes...`,
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        let printedCount = 0;

        const printNextBarcode = () => {
            if (printedCount >= barcodesToPrint.length) {
                Swal.fire('Success', `Printed ${printedCount} barcodes successfully`, 'success');
                return;
            }

            const barcode = barcodesToPrint[printedCount];
            printedCount++;

            const printContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Barcode - ${barcode.barcode_number}</title>
                    <style>
                        @media print {
                            @page {
                                margin: 0;
                                padding: 0;
                                size: 40mm 30mm;
                            }
                            body {
                                margin: 0;
                                padding: 1mm;
                                width: 40mm;
                                height: 30mm;
                                font-family: Arial, sans-serif;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                            }
                            .barcode-sticker {
                                width: 38mm;
                                height: 28mm;
                                border: 1px solid #000;
                                padding: 1mm;
                                text-align: center;
                                display: flex;
                                flex-direction: column;
                                justify-content: space-between;
                            }
                            .product-info {
                                font-size: 8px;
                                line-height: 1;
                                margin-bottom: 1mm;
                            }
                            .product-name {
                                font-weight: bold;
                                margin-bottom: 0.5mm;
                            }
                            .variant-info {
                                font-size: 7px;
                                color: #666;
                                margin-bottom: 0.5mm;
                            }
                            .barcode-container {
                                margin: 1mm 0;
                            }
                            .barcode-number {
                                font-size: 10px;
                                font-weight: bold;
                                font-family: 'Courier New', monospace;
                                letter-spacing: 0.5px;
                                margin-top: 1mm;
                            }
                            .sku-info {
                                font-size: 7px;
                                color: #333;
                            }
                        }
                    </style>
                </head>
                <body>
                    <div class="barcode-sticker">
                        <div class="product-info">
                            <div class="product-name">${product.name.substring(0, 20)}</div>
                            ${barcode.variant ? `<div class="variant-info">${barcode.variant.variant_name.substring(0, 15)}</div>` : ''}
                        </div>
                        <div class="barcode-container">
                            <svg id="barcode-svg"></svg>
                        </div>
                        <div class="barcode-number">${barcode.barcode_number}</div>
                        <div class="sku-info">SKU: ${product.sku}</div>
                    </div>
                    <script>
                        setTimeout(() => {
                            JsBarcode('#barcode-svg', '${barcode.barcode_number}', {
                                format: "CODE128",
                                width: 1.5,
                                height: 25,
                                displayValue: false,
                                margin: 0
                            });
                            window.print();
                            setTimeout(() => {
                                window.close();
                            }, 100);
                        }, 100);
                    </script>
                </body>
                </html>
            `;

            const printWindow = window.open('', '_blank', `width=40mm,height=30mm`);
            printWindow.document.write(printContent);
            printWindow.document.close();

            // Print next barcode after a delay
            setTimeout(printNextBarcode, 1500);
        };

        // Start printing
        printNextBarcode();
    };

    return (
        <div className="barcode-modal-overlay">
            <div className="barcode-modal barcode-modal-large">
                <div className="barcode-modal-header">
                    <h2>Barcode History - {product.name}</h2>
                    <button className="barcode-modal-close" onClick={onClose}>×</button>
                </div>
                <div className="barcode-modal-body">
                    <div className="barcode-history-stats">
                        <div className="barcode-stat-item">
                            <span className="barcode-stat-value">{statistics.total_barcodes}</span>
                            <span className="barcode-stat-label">Total</span>
                        </div>
                        <div className="barcode-stat-item">
                            <span className="barcode-stat-value barcode-stat-used">{statistics.used_barcodes}</span>
                            <span className="barcode-stat-label">Used</span>
                        </div>
                        <div className="barcode-stat-item">
                            <span className="barcode-stat-value barcode-stat-unused">{statistics.unused_barcodes}</span>
                            <span className="barcode-stat-label">Unused</span>
                        </div>
                    </div>

                    <div className="barcode-history-list">
                        <div className="barcode-list-header">
                            <div className="barcode-select-all">
                                <input
                                    type="checkbox"
                                    checked={selectedBarcodes.length === barcodes.length && barcodes.length > 0}
                                    onChange={handleSelectAll}
                                />
                                <span>Select All</span>
                            </div>
                            <button className="barcode-btn-print" onClick={printBarcodes}>
                                🖨️ Print Selected ({selectedBarcodes.length})
                            </button>
                        </div>

                        <div className="barcode-history-grid">
                            {barcodes.map(barcode => (
                                <div key={barcode.id} className="barcode-history-item">
                                    <input
                                        type="checkbox"
                                        checked={selectedBarcodes.includes(barcode.id)}
                                        onChange={() => {
                                            if (selectedBarcodes.includes(barcode.id)) {
                                                setSelectedBarcodes(selectedBarcodes.filter(id => id !== barcode.id));
                                            } else {
                                                setSelectedBarcodes([...selectedBarcodes, barcode.id]);
                                            }
                                        }}
                                    />
                                    <div className="barcode-history-content">
                                        <div className="barcode-history-number">{barcode.barcode_number}</div>
                                        <div className="barcode-history-meta">
                                            {barcode.variant && (
                                                <span className="barcode-variant-tag">{barcode.variant.variant_name}</span>
                                            )}
                                            <span className={`barcode-history-status ${barcode.is_used ? 'barcode-status-used' : 'barcode-status-unused'}`}>
                                                {barcode.is_used ? 'Used' : 'Unused'}
                                            </span>
                                        </div>
                                        <div className="barcode-history-date">{formatDate(barcode.created_at)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Generated Barcodes View Component
const BarcodeGeneratedView = ({ generatedBarcodes, onClose, onExportExcel, onBoxHeroPrint }) => {
    const [selectedBarcodes, setSelectedBarcodes] = useState([]);
    const [isPrinting, setIsPrinting] = useState(false);

    const isVariantResponse = generatedBarcodes.variants !== undefined;
    const product = generatedBarcodes.product;
    const allBarcodes = isVariantResponse 
        ? generatedBarcodes.variants.flatMap(variant => variant.barcodes)
        : generatedBarcodes.barcodes;

    const handleSelectAll = () => {
        if (selectedBarcodes.length === allBarcodes.length) {
            setSelectedBarcodes([]);
        } else {
            setSelectedBarcodes(allBarcodes.map(b => b.id));
        }
    };

    const printSingleBarcode = (barcode) => {
        return new Promise((resolve) => {
            const printContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Barcode Sticker</title>
                    <style>
                        @page {
                            size: 40mm 30mm;
                            margin: 0;
                            padding: 0;
                        }
                        
                        @media print {
                            body {
                                margin: 0 !important;
                                padding: 0 !important;
                                width: 40mm !important;
                                height: 30mm !important;
                            }
                        }
                        
                        body {
                            margin: 0;
                            padding: 0;
                            width: 40mm;
                            height: 30mm;
                            font-family: Arial, sans-serif;
                            background: white;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                        }
                        
                        .sticker {
                            width: 38mm;
                            height: 28mm;
                            padding: 1mm;
                            border: 1px solid #000;
                            text-align: center;
                            display: flex;
                            flex-direction: column;
                            justify-content: space-between;
                            box-sizing: border-box;
                        }
                        
                        .header {
                            flex-shrink: 0;
                        }
                        
                        .product-name {
                            font-size: 8px;
                            font-weight: bold;
                            line-height: 1;
                            margin: 0 0 1px 0;
                        }
                        
                        .variant-info {
                            font-size: 6px;
                            color: #666;
                            line-height: 1;
                            margin: 0;
                        }
                        
                        .barcode-section {
                            flex: 1;
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            justify-content: center;
                            margin: 2px 0;
                        }
                        
                        .barcode-number {
                            font-size: 8px;
                            font-weight: bold;
                            font-family: 'Courier New', monospace;
                            margin: 2px 0 0 0;
                        }
                        
                        .footer {
                            flex-shrink: 0;
                        }
                        
                        .sku {
                            font-size: 6px;
                            color: #333;
                            line-height: 1;
                            margin: 0;
                        }
                        
                        .batch {
                            font-size: 5px;
                            color: #999;
                            line-height: 1;
                            margin: 0;
                        }
                    </style>
                </head>
                <body>
                    <div class="sticker">
                        <div class="header">
                            <div class="product-name">${product.name}</div>
                            ${barcode.variant ? `<div class="variant-info">${barcode.variant.variant_name}</div>` : ''}
                        </div>
                        
                        <div class="barcode-section">
                            <svg id="barcode-svg"></svg>
                            <div class="barcode-number">${barcode.barcode_number}</div>
                        </div>
                        
                        <div class="footer">
                            <div class="sku">SKU: ${product.sku}</div>
                            <div class="batch">Batch: ${barcode.batch_number} | Seq: ${barcode.sequence_number}</div>
                        </div>
                    </div>
                    
                    <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
                    <script>
                        JsBarcode('#barcode-svg', '${barcode.barcode_number}', {
                            format: "CODE128",
                            width: 1,
                            height: 18,
                            displayValue: false,
                            margin: 0
                        });
                        
                        const svg = document.getElementById('barcode-svg');
                        if (svg) {
                            svg.style.maxWidth = '34mm';
                            svg.style.maxHeight = '14mm';
                        }
                        
                        setTimeout(() => {
                            window.print();
                            setTimeout(() => {
                                window.close();
                            }, 100);
                        }, 200);
                    </script>
                </body>
                </html>
            `;

            const printWindow = window.open('', '_blank');
            printWindow.document.write(printContent);
            printWindow.document.close();
            
            setTimeout(() => {
                if (!printWindow.closed) {
                    printWindow.close();
                }
                resolve();
            }, 3000);
        });
    };

    const printSelectedBarcodes = async () => {
        const barcodesToPrint = selectedBarcodes.length > 0
            ? allBarcodes.filter(b => selectedBarcodes.includes(b.id))
            : allBarcodes;

        if (barcodesToPrint.length === 0) {
            Swal.fire('Info', 'No barcodes selected to print', 'info');
            return;
        }

        setIsPrinting(true);

        try {
            for (let i = 0; i < barcodesToPrint.length; i++) {
                await printSingleBarcode(barcodesToPrint[i]);
            }
            Swal.fire('Success', `Printed ${barcodesToPrint.length} barcodes`, 'success');
        } catch (error) {
            Swal.fire('Error', 'Failed to print some barcodes', 'error');
        } finally {
            setIsPrinting(false);
        }
    };

    const copyBarcodeNumbers = async () => {
        const barcodesToCopy = selectedBarcodes.length > 0
            ? allBarcodes.filter(b => selectedBarcodes.includes(b.id))
            : allBarcodes;

        const barcodeNumbers = barcodesToCopy.map(b => b.barcode_number).join('\n');
        
        try {
            await navigator.clipboard.writeText(barcodeNumbers);
            Swal.fire('Copied!', `${barcodesToCopy.length} barcode numbers copied`, 'success');
        } catch (error) {
            Swal.fire('Error', 'Failed to copy barcode numbers', 'error');
        }
    };

    return (
        <div className="barcode-generated-view">
            <div className="barcode-view-header">
                <div className="barcode-header-info">
                    <h2>Generated Barcodes</h2>
                    <p>{allBarcodes.length} barcodes generated for {product.name}</p>
                    {isVariantResponse && (
                        <div className="barcode-variant-summary">
                            {generatedBarcodes.variants.map(variant => (
                                <span key={variant.variant_id} className="barcode-variant-summary-item">
                                    {variant.variant.variant_name}: {variant.quantity}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                <div className="barcode-view-actions">
                    <button className="barcode-btn-secondary" onClick={onClose}>
                        ← Back to Products
                    </button>
                    <button 
                        className="barcode-btn-copy" 
                        onClick={copyBarcodeNumbers}
                        disabled={allBarcodes.length === 0}
                    >
                        📋 Copy Numbers
                    </button>
                    <button 
                        className="barcode-btn-excel"
                        onClick={onExportExcel}
                        disabled={allBarcodes.length === 0}
                    >
                        📊 Export Excel
                    </button>
                    <button 
                        className="barcode-btn-boxhero"
                        onClick={onBoxHeroPrint}
                        disabled={allBarcodes.length === 0}
                    >
                        🚀 BoxHero Print
                    </button>
                    <button 
                        className={`barcode-btn-print ${isPrinting ? 'barcode-printing' : ''}`}
                        onClick={printSelectedBarcodes}
                        disabled={isPrinting || allBarcodes.length === 0}
                    >
                        {isPrinting ? '🖨️ Printing...' : '🖨️ Print'}
                    </button>
                </div>
            </div>

            <div className="barcode-controls">
                <div className="barcode-select-controls">
                    <input
                        type="checkbox"
                        checked={selectedBarcodes.length === allBarcodes.length && allBarcodes.length > 0}
                        onChange={handleSelectAll}
                    />
                    <span>
                        {selectedBarcodes.length > 0 
                            ? `${selectedBarcodes.length} selected` 
                            : 'Select All'
                        }
                    </span>
                </div>
            </div>

            <div className="barcode-display-grid">
                {allBarcodes.map(barcode => (
                    <div key={barcode.id} className="barcode-display-card">
                        <input
                            type="checkbox"
                            checked={selectedBarcodes.includes(barcode.id)}
                            onChange={() => {
                                if (selectedBarcodes.includes(barcode.id)) {
                                    setSelectedBarcodes(selectedBarcodes.filter(id => id !== barcode.id));
                                } else {
                                    setSelectedBarcodes([...selectedBarcodes, barcode.id]);
                                }
                            }}
                        />
                        <div className="barcode-display-content">
                            <div className="barcode-display-number">{barcode.barcode_number}</div>
                            <div className="barcode-display-details">
                                {barcode.variant && (
                                    <div className="barcode-display-variant">Variant: {barcode.variant.variant_name}</div>
                                )}
                                <div className="barcode-display-batch">Batch: #{barcode.batch_number}</div>
                                <div className="barcode-display-sequence">Sequence: {barcode.sequence_number}</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {isPrinting && (
                <div className="barcode-printing-overlay">
                    <div className="barcode-printing-message">
                        <div className="barcode-printing-spinner"></div>
                        <p>Printing barcodes...</p>
                        <p className="barcode-printing-note">Please don't close this window</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductQRManagement;